import { Box, Flex, Image, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React from 'react'
import { RiArrowRightLine } from 'react-icons/ri'
import { CopyToClipboardButton } from 'components/CopyToClipboardButton'
import { InfoPopup } from 'components/InfoPopup'
import { Link } from 'components/Link'
import { Pagination } from 'components/Pagination'
import { Table } from 'components/Table'
import {
  DETAIL_CARD_PADDING_X,
  NOT_APPLICABLE,
  PAGE_LENGTH,
  TIMESTAMP_COLUMN_FORMAT,
  TRUNCATED_MOBILE_TEXT_WIDTH,
} from 'constants/common'
import { Routes } from 'constants/routes'
import { useGetAddressInternalTransactions } from 'generated/reactQueryClient'
import type { AddressTransactionInternalListItem as InternalTxItem } from 'generated/reactQueryClient'
import { useCursorPagination } from 'hooks/useCursorPagination'
import { useThemeColors } from 'hooks/useThemeColors'
import { useTransactionHashTruncateOptions } from 'hooks/useTransactionHashTruncateOptions'
import { getFormattedNumber, getTruncatedText, weiToEth } from 'utils/common'
import {
  getTimestampColumnFormatValue,
  getTimestampColumnTooltipFormatValue,
  getTimestampColumnTooltipText,
} from 'utils/dayjs'

export const InternalTxns = () => {
  const COLORS = useThemeColors()
  const TRANSACTION_HASH_TRUNCATE_OPTIONS = useTransactionHashTruncateOptions()
  const { query } = useRouter()
  const { cursor, setCursor, direction, setDirection } = useCursorPagination()

  const [isAgeTimestampFormat, setIsAgeTimestampFormat] = React.useState<boolean>(true)

  const { data: { data, paginationContext } = { data: [] }, isLoading } =
    useGetAddressInternalTransactions(query.hash as string, {
      limit: PAGE_LENGTH,
      ...(cursor && { [direction]: cursor }),
    })

  const timestampFormat = isAgeTimestampFormat
    ? TIMESTAMP_COLUMN_FORMAT.AGE
    : TIMESTAMP_COLUMN_FORMAT.DATE_TIME

  return (
    <Box mx={-DETAIL_CARD_PADDING_X}>
      <Flex
        h={55}
        px={5}
        py={2}
        bgColor={COLORS.grey07}
        borderBottom="1px solid"
        borderColor={COLORS.grey06}
      >
        <Pagination
          ml="auto"
          isDisabled={isLoading || data?.length === 0}
          hasNextPage={!!paginationContext?.next}
          hasPreviousPage={!!paginationContext?.previous}
          onNextPage={() => {
            setDirection('next')
            setCursor(paginationContext?.next ?? '')
          }}
          onPreviousPage={() => {
            setDirection('previous')
            setCursor(paginationContext?.previous ?? '')
          }}
        />
      </Flex>
      <Table
        variant="secondary"
        data={data}
        hasTopContent={false}
        isLoading={isLoading}
        withTopRadius={false}
        renderMobileRow={(row: InternalTxItem, index) => (
          <Table.MobileRow key={`${row.l2ParentTxHash}-${row.timestamp}-${index}`}>
            {({ isExpanded }) => (
              <Flex flex="1" flexDirection="column" textAlign="left" gap={3}>
                <Table.MobileRowCell label="Parent Txn Hash:">
                  <Link
                    href={Routes.transactionDetail({
                      txHash: String(row.l2ParentTxHash),
                      activeTab: 0,
                    })}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {getTruncatedText(row.l2ParentTxHash, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)}
                  </Link>
                </Table.MobileRowCell>
                <Table.AnimatedMobileRowCell label="Block L2:" isVisible={isExpanded}>
                  <Link
                    href={Routes.blockDetail({ id: String(row.l2BlockIndex) })}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {row.l2BlockIndex}
                  </Link>
                </Table.AnimatedMobileRowCell>
                <Table.AnimatedMobileRowCell
                  label={
                    data?.some((tx) => tx.timestamp) ? (
                      <Table.InteractiveMobileHeaderCell
                        variant="text3regular"
                        onClick={() => {
                          setIsAgeTimestampFormat((prev) => !prev)
                        }}
                      >
                        {timestampFormat}:
                      </Table.InteractiveMobileHeaderCell>
                    ) : (
                      `${timestampFormat}:`
                    )
                  }
                  isVisible={isExpanded}
                >
                  <InfoPopup
                    title={
                      timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                        ? 'Internal Txn Datetime:'
                        : 'Internal Txn Age:'
                    }
                    triggerElement={
                      <Text
                        variant="text3regular"
                        color={COLORS.grey03}
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        {row.timestamp
                          ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                          : NOT_APPLICABLE}
                      </Text>
                    }
                  >
                    {row.timestamp
                      ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                      : NOT_APPLICABLE}
                  </InfoPopup>
                </Table.AnimatedMobileRowCell>
                <Table.MobileRowCell label="From:">
                  <Link
                    href={Routes.addressDetail({ hash: row.from })}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {getTruncatedText(row.from, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)}
                  </Link>
                </Table.MobileRowCell>
                <Table.MobileRowCell label="To:">
                  <Link
                    href={Routes.addressDetail({ hash: row.to })}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {getTruncatedText(row.to, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)}
                  </Link>
                </Table.MobileRowCell>
                <Table.MobileRowCell label="Value:">
                  <Flex>
                    <Image alt="Ethereum2" src="/assets/ethereum2.svg" />
                    <Text
                      ml={1}
                      variant="text3regular"
                      w={TRUNCATED_MOBILE_TEXT_WIDTH}
                      isTruncated
                      whiteSpace="nowrap"
                    >
                      {getFormattedNumber(weiToEth(row.value))} ETH
                    </Text>
                  </Flex>
                </Table.MobileRowCell>
              </Flex>
            )}
          </Table.MobileRow>
        )}
        renderTableHeader={() => (
          <>
            <Table.HeaderCell>Parent Txn Hash</Table.HeaderCell>
            <Table.HeaderCell>Block L2</Table.HeaderCell>
            {data?.some((tx) => tx.timestamp) ? (
              <Table.InteractiveHeaderCell
                tooltipText={getTimestampColumnTooltipText(timestampFormat)}
                onClick={() => {
                  setIsAgeTimestampFormat((prev) => !prev)
                }}
              >
                {timestampFormat}
              </Table.InteractiveHeaderCell>
            ) : (
              <Table.HeaderCell>{timestampFormat}</Table.HeaderCell>
            )}
            <Table.HeaderCell>From</Table.HeaderCell>
            <Table.HeaderCell w={120} />
            <Table.HeaderCell>To</Table.HeaderCell>
            <Table.HeaderCell>Value</Table.HeaderCell>
          </>
        )}
        renderTableRow={(row: InternalTxItem, index) => (
          <Table.Row key={`${row.l2ParentTxHash}-${row.timestamp}-${index}`}>
            <Table.RowCell>
              <Flex alignItems="center" gap={1}>
                <Link
                  href={Routes.transactionDetail({
                    txHash: String(row.l2ParentTxHash),
                    activeTab: 0,
                  })}
                  maxW={300}
                  isTruncated
                >
                  {row.l2ParentTxHash}
                </Link>
                <CopyToClipboardButton text={row.l2ParentTxHash} />
              </Flex>
            </Table.RowCell>
            <Table.RowCell>
              <Link href={Routes.blockDetail({ id: String(row.l2BlockIndex) })}>
                {row.l2BlockIndex}
              </Link>
            </Table.RowCell>
            <Table.RowCell>
              <InfoPopup
                title={
                  timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                    ? 'Internal Txn Datetime:'
                    : 'Internal Txn Age:'
                }
                triggerElement={
                  <Text variant="text3regular" color={COLORS.grey03}>
                    {row.timestamp
                      ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                      : NOT_APPLICABLE}
                  </Text>
                }
              >
                {row.timestamp
                  ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                  : NOT_APPLICABLE}
              </InfoPopup>
            </Table.RowCell>
            <Table.RowCell>
              <Flex alignItems="center" gap={1}>
                <Link href={Routes.addressDetail({ hash: row.from })}>
                  {getTruncatedText(row.from)}
                </Link>
                <CopyToClipboardButton text={row.from} />
              </Flex>
            </Table.RowCell>
            <Table.RowCell>
              <RiArrowRightLine size={16} color={COLORS.zircuitPrimary} />
            </Table.RowCell>
            <Table.RowCell>
              <Flex alignItems="center" gap={1}>
                <Link href={Routes.addressDetail({ hash: row.to })}>
                  {getTruncatedText(row.to)}
                </Link>
                <CopyToClipboardButton text={row.to} />
              </Flex>
            </Table.RowCell>
            <Table.RowCell>
              <Flex>
                <Image alt="Ethereum2" src="/assets/ethereum2.svg" />
                <Text ml={1} variant="text3regular">
                  {getFormattedNumber(weiToEth(row.value))} ETH
                </Text>
              </Flex>
            </Table.RowCell>
          </Table.Row>
        )}
      />
    </Box>
  )
}
